import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as echarts from 'echarts';
import { Subject, Subscription, auditTime, filter, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';

type SelectionState = 'checked' | 'partial' | 'none';

interface FormatRule {
  type?: 'text' | 'number' | 'currency' | 'percent' | 'compact';
  decimals?: number;
  prefix?: string;
  suffix?: string;
  currency?: string;
  locale?: string;
  scale?: number;
  template?: string;
}

interface NormalizedNode {
  id: string;
  label: string;
  value: number;
  score: any;
  color?: string;
  status?: string;
  parentId?: string;
  level: number;
  raw: any;
  children: NormalizedNode[];
}

interface VisibleNode {
  node: NormalizedNode;
  level: number;
}

interface SummaryItem {
  id: string;
  label: string;
  score: any;
  color?: string;
  textColor?: string;
  nodeId?: string;
  raw: any;
}

interface InfoCardItem {
  label: string;
  value: string;
  rawValue: any;
}

interface InfoCard {
  id: string;
  title: string;
  position: any;
  items: InfoCardItem[];
  raw: any;
}

interface DonutSegment {
  nodeId: string;
  label: string;
  value: number;
  color: string;
  raw: any;
}

@Component({
  selector: 'app-mine-assessment-drilldown-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    DataErrorComponent,
  ],
  templateUrl: './mine-assessment-drilldown-dashboard.component.html',
  styleUrl: './mine-assessment-drilldown-dashboard.component.scss',
  animations: [
    trigger('fadePanel', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('180ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('140ms ease-in', style({ opacity: 0, transform: 'translateY(8px)' })),
      ]),
    ]),
  ],
})
export class MineAssessmentDrilldownDashboardComponent implements AfterViewInit, OnDestroy {
  @Input() data: any;
  @ViewChild('chartHost') chartHost?: ElementRef<HTMLDivElement>;

  loading = false;
  hasError = false;
  errorMessage = '';
  searchTerm = '';

  rootNodes: NormalizedNode[] = [];
  visibleNodes: VisibleNode[] = [];
  summaryItems: SummaryItem[] = [];
  infoCards: InfoCard[] = [];
  chartSegments: DonutSegment[] = [];
  centerLabelLines: string[] = [];

  private chart?: echarts.ECharts;
  private rootPayload: any;
  private expandedNodeIds = new Set<string>();
  private selectedNodeIds = new Set<string>();
  private nodeLookup = new Map<string, NormalizedNode>();
  private focusedNodeId?: string;
  private initializedState = false;
  private destroy$ = new Subject<void>();
  private reflowSub?: Subscription;

  constructor(
    private configService: ConfigService,
    private filterState: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private cdr: ChangeDetectorRef,
  ) {}

  get dashboardTitle(): string {
    return this.data?.title || this.dashboardConfig?.title || '';
  }

  get chartDomId(): string {
    return `${this.data?.widgetId || 'mine-assessment-drilldown-dashboard'}-donut`;
  }

  get treeTitle(): string {
    return this.dashboardConfig?.tree?.title || this.dashboardConfig?.treeTitle || 'Assessment Tree';
  }

  get treeSearchEnabled(): boolean {
    return this.dashboardConfig?.tree?.enableSearch !== false;
  }

  get treeSearchPlaceholder(): string {
    return this.dashboardConfig?.tree?.searchPlaceholder || 'Search';
  }

  get summaryTreeEnabled(): boolean {
    return this.dashboardConfig?.tree?.displayMode === 'summary-dropdown';
  }

  get showTreePanel(): boolean {
    return this.dashboardConfig?.tree?.showPanel !== false && !this.summaryTreeEnabled;
  }

  get selectionMode(): 'single' | 'multi' {
    return this.dashboardConfig?.selection?.mode === 'single' ? 'single' : 'multi';
  }

  get visibleNodeCount(): number {
    return this.visibleNodes.length;
  }

  ngAfterViewInit(): void {
    this.loadData();

    this.filterState.filter$
      .pipe(
        auditTime(300),
        filter((filters) => !!filters),
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => {
        if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) return;
        this.loadData(filters);
      });

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.resizeChart());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reflowSub?.unsubscribe();
    this.chart?.dispose();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resizeChart();
  }

  onSearchChange(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement)?.value || '';
    this.rebuildVisibleNodes();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchTerm = '';
    this.rebuildVisibleNodes();
  }

  toggleNodeExpansion(node: NormalizedNode, event?: Event): void {
    event?.stopPropagation();

    if (!node.children.length) {
      this.focusNode(node);
      return;
    }

    if (this.expandedNodeIds.has(node.id)) {
      this.expandedNodeIds.delete(node.id);
      if (this.focusedNodeId === node.id && this.dashboardConfig?.selection?.updateChartOnExpand !== false) {
        this.focusedNodeId = undefined;
      }
    } else {
      this.expandedNodeIds.add(node.id);
      if (this.dashboardConfig?.selection?.updateChartOnExpand !== false) {
        this.focusNode(node, false);
      }
    }

    this.rebuildVisibleNodes();
    this.refreshContext();
  }

  onNodeRowClick(node: NormalizedNode): void {
    const rowAction = this.dashboardConfig?.tree?.rowClickAction || 'select';
    if (rowAction === 'expand' && node.children.length) {
      this.toggleNodeExpansion(node);
      return;
    }
    this.toggleNodeSelection(node);
  }

  toggleNodeSelection(node: NormalizedNode, event?: Event): void {
    event?.stopPropagation();

    const nextSelected = !this.isNodeChecked(node);
    const cascade = this.dashboardConfig?.selection?.cascade !== false;
    const affectedIds = cascade ? [node.id, ...this.collectDescendantIds(node)] : [node.id];

    if (this.selectionMode === 'single') {
      this.selectedNodeIds.clear();
    }

    affectedIds.forEach((id) => {
      if (nextSelected) {
        this.selectedNodeIds.add(id);
      } else {
        this.selectedNodeIds.delete(id);
      }
    });

    if (this.selectedNodeIds.size === 0) {
      this.focusedNodeId = undefined;
    } else if (nextSelected) {
      this.focusedNodeId = node.id;
    } else {
      this.focusedNodeId = this.getTopSelectedNodes()[0]?.id;
    }

    this.rebuildVisibleNodes();
    this.refreshContext();
    this.emitSelection();
  }

  selectSummaryItem(item: SummaryItem): void {
    const node = this.getSummaryNode(item);
    if (node) {
      if (this.summaryTreeEnabled && node.children.length) {
        const wasSelected = this.isNodeChecked(node);
        this.toggleNodeSelection(node);
        if (!wasSelected) {
          this.expandedNodeIds.add(node.id);
          this.rebuildVisibleNodes();
        }
      } else {
        this.toggleNodeSelection(node);
        this.ensureAncestorsExpanded(node);
        this.rebuildVisibleNodes();
      }
      return;
    }

    this.focusedNodeId = undefined;
    this.refreshContext();
  }

  onSummaryChildClick(node: NormalizedNode, event?: Event): void {
    event?.stopPropagation();
    if (node.children.length) {
      const wasSelected = this.isNodeChecked(node);
      this.toggleNodeSelection(node);
      if (!wasSelected) {
        this.expandedNodeIds.add(node.id);
        this.rebuildVisibleNodes();
      }
      return;
    }
    this.toggleNodeSelection(node);
  }

  getSummaryNode(item: SummaryItem): NormalizedNode | undefined {
    if (item.nodeId) {
      return this.nodeLookup.get(item.nodeId) || this.findNodeByLabel(item.nodeId);
    }

    const summaryCfg = this.dashboardConfig?.summary || {};
    const linkPath = summaryCfg.nodeLinkKey || summaryCfg.nodeLabelKey;
    const linkValue = linkPath ? this.getPath(item.raw, linkPath) : item.label;
    return this.findNodeByLabel(String(linkValue || item.label));
  }

  getSummaryDropdownNode(item: SummaryItem): NormalizedNode | undefined {
    return this.summaryTreeEnabled ? this.getSummaryNode(item) : undefined;
  }

  hasSummaryChildren(item: SummaryItem): boolean {
    return !!this.getSummaryNode(item)?.children?.length;
  }

  isSummarySelected(item: SummaryItem): boolean {
    const node = this.getSummaryNode(item);
    return !!node && this.getSelectionState(node) === 'checked';
  }

  isSummaryPartial(item: SummaryItem): boolean {
    const node = this.getSummaryNode(item);
    return !!node && this.getSelectionState(node) === 'partial';
  }

  isExpanded(node: NormalizedNode): boolean {
    return this.expandedNodeIds.has(node.id);
  }

  isNodeChecked(node: NormalizedNode): boolean {
    const cascade = this.dashboardConfig?.selection?.cascade !== false;
    if (!cascade || !node.children.length) return this.selectedNodeIds.has(node.id);
    return [node.id, ...this.collectDescendantIds(node)].every((id) => this.selectedNodeIds.has(id));
  }

  isNodePartial(node: NormalizedNode): boolean {
    if (!node.children.length) return false;
    const ids = [node.id, ...this.collectDescendantIds(node)];
    const selectedCount = ids.filter((id) => this.selectedNodeIds.has(id)).length;
    return selectedCount > 0 && selectedCount < ids.length;
  }

  getSelectionState(node: NormalizedNode): SelectionState {
    if (this.isNodeChecked(node)) return 'checked';
    if (this.isNodePartial(node)) return 'partial';
    return 'none';
  }

  getNodeIndent(level: number): number {
    const base = Number(this.dashboardConfig?.tree?.indentSize ?? 18);
    return level * base;
  }

  formatNodeValue(node: NormalizedNode): string {
    const format = this.dashboardConfig?.tree?.valueFormat || this.dashboardConfig?.donut?.valueFormat;
    return this.formatValue(node.value, format);
  }

  getSummaryScore(item: SummaryItem): string {
    const format = this.dashboardConfig?.summary?.scoreFormat || { type: 'number', decimals: 1 };
    return this.formatValue(item.score, format);
  }

  getSummaryStyle(item: SummaryItem): Record<string, string> {
    const threshold = this.getThresholdStyle(item.score);
    return {
      'background-color': item.color || threshold['background-color'] || '#d8d8d8',
      color: item.textColor || threshold['color'] || '#1f1f1f',
    };
  }

  getInfoCardStyle(card: InfoCard): Record<string, string> {
    const position = card.position;
    const baseStyle = {
      '--connector-color': this.resolveInfoCardAccent(card),
    } as Record<string, string>;

    if (position && typeof position === 'object') {
      return { ...baseStyle, ...position };
    }

    const key = String(position || 'auto').toLowerCase();
    const configured = this.dashboardConfig?.infoCards?.positionMap?.[key];
    if (configured && typeof configured === 'object') {
      return { ...baseStyle, ...configured };
    }

    const defaults: Record<string, Record<string, string>> = {
      top: { top: '0', left: '50%', transform: 'translateX(-50%)' },
      right: { top: '24%', right: '0' },
      'right-bottom': { right: '0', bottom: '17%' },
      bottom: { bottom: '0', left: '50%', transform: 'translateX(-50%)' },
      left: { top: '24%', left: '0' },
      'left-bottom': { left: '0', bottom: '17%' },
      'top-left': { top: '0', left: '0' },
      'top-right': { top: '0', right: '0' },
    };

    return { ...baseStyle, ...(defaults[key] || defaults['top']) };
  }

  getInfoCardClass(card: InfoCard): string {
    const position = typeof card.position === 'string' ? card.position : 'custom';
    return `insight-card--${position.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  trackByNodeId(_: number, row: VisibleNode): string {
    return row.node.id;
  }

  trackBySummaryId(_: number, item: SummaryItem): string {
    return item.id;
  }

  trackByCardId(_: number, card: InfoCard): string {
    return card.id;
  }

  trackByLabel(_: number, item: InfoCardItem): string {
    return `${item.label}:${item.value}`;
  }

  protected get dashboardConfig(): any {
    return this.data?.dashboardConfig || this.data?.mineAssessmentConfig || {};
  }

  protected loadData(filters?: any): void {
    const inlineData = this.data?.widgetData || this.data?.data || this.data?.dashboardData;

    if (!this.data?.apiUrl && inlineData) {
      this.consumeResponse(inlineData);
      return;
    }

    if (!this.data?.apiUrl) {
      this.hasError = true;
      this.errorMessage = 'No data source configured.';
      return;
    }

    this.loading = true;
    this.hasError = false;

    this.configService
      .getConfig(this.data.apiUrl, this.data, filters, this.data?.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.consumeResponse(response);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.hasError = true;
          this.errorMessage = this.dashboardConfig?.messages?.loadError || 'Failed to load assessment data.';
          this.cdr.detectChanges();
        },
      });
  }

  protected consumeResponse(response: any): void {
    const payload = this.resolvePayload(response);
    if (!payload) {
      this.hasError = true;
      this.errorMessage = this.dashboardConfig?.messages?.empty || 'No assessment data found.';
      return;
    }

    const previousSelected = new Set(this.selectedNodeIds);
    const previousExpanded = new Set(this.expandedNodeIds);

    this.rootPayload = payload;
    this.nodeLookup.clear();
    this.rootNodes = this.normalizeTree(payload);

    if (!this.rootNodes.length) {
      this.hasError = true;
      this.errorMessage = this.dashboardConfig?.messages?.empty || 'No assessment data found.';
      return;
    }

    this.selectedNodeIds = this.keepValidIds(previousSelected);
    this.expandedNodeIds = this.keepValidIds(previousExpanded);

    if (!this.initializedState) {
      this.applyInitialState();
      this.initializedState = true;
    }

    this.summaryItems = this.normalizeSummary(payload);
    this.rebuildVisibleNodes();
    this.refreshContext();
    this.hasError = false;
  }

  protected resolvePayload(response: any): any {
    const paths = [
      this.dashboardConfig?.dataPath,
      this.data?.dataKey,
      this.data?.value,
      'responseData',
    ].filter(Boolean);

    for (const path of paths) {
      const resolved = this.getPath(response, path);
      if (resolved !== undefined && resolved !== null) {
        return Array.isArray(resolved) && resolved.length === 1 ? resolved[0] : resolved;
      }
    }

    return response;
  }

  protected normalizeTree(payload: any): NormalizedNode[] {
    const treeCfg = this.dashboardConfig?.tree || {};
    const sourcePath = treeCfg.sourcePath || this.dashboardConfig?.treePath || 'tree';
    const rawTree = this.getPath(payload, sourcePath) ?? this.getPath(payload, 'drillDownTree');
    const list = Array.isArray(rawTree) ? rawTree : rawTree ? [rawTree] : [];

    return list.map((item, index) => this.normalizeNode(item, index, 0));
  }

  protected normalizeNode(raw: any, index: number, level: number, parent?: NormalizedNode): NormalizedNode {
    const mapping = this.dashboardConfig?.nodeMapping || {};
    const childrenPath = mapping.children || 'children';
    const label = String(
      this.getPath(raw, mapping.label || 'label')
        ?? this.getPath(raw, 'name')
        ?? this.getPath(raw, 'title')
        ?? `Node ${index + 1}`,
    );
    const configuredId = this.getPath(raw, mapping.id || 'id');
    const id = String(configuredId ?? `${parent?.id || 'root'}-${this.slugify(label)}-${index}`);
    const rawValue = this.getPath(raw, mapping.value || 'weightage')
      ?? this.getPath(raw, 'value')
      ?? this.getPath(raw, 'score')
      ?? 0;
    const rawChildren = this.getPath(raw, childrenPath);

    const node: NormalizedNode = {
      id,
      label,
      value: this.toNumber(rawValue),
      score: this.getPath(raw, mapping.score || 'score'),
      color: this.getPath(raw, mapping.color || 'color'),
      status: this.getPath(raw, mapping.status || 'status'),
      parentId: parent?.id,
      level,
      raw,
      children: [],
    };

    node.children = (Array.isArray(rawChildren) ? rawChildren : [])
      .map((child, childIndex) => this.normalizeNode(child, childIndex, level + 1, node));

    if (!node.value && node.children.length) {
      node.value = node.children.reduce((sum, child) => sum + child.value, 0);
    }

    this.nodeLookup.set(node.id, node);
    return node;
  }

  protected normalizeSummary(payload: any): SummaryItem[] {
    const summaryCfg = this.dashboardConfig?.summary || {};
    const mapping = summaryCfg.mapping || {};
    const sourcePath = summaryCfg.sourcePath || this.dashboardConfig?.summaryPath || 'summary';
    const rawSummary = this.getPath(payload, sourcePath);
    const source = Array.isArray(rawSummary) && rawSummary.length
      ? rawSummary
      : this.rootNodes.map((node) => node.raw);

    return source.map((item: any, index: number) => {
      const label = String(
        this.getPath(item, mapping.label || 'label')
          ?? this.getPath(item, 'name')
          ?? this.getPath(item, 'title')
          ?? `Parameter ${index + 1}`,
      );
      const nodeId = this.getPath(item, mapping.nodeId || 'nodeId');
      return {
        id: String(this.getPath(item, mapping.id || 'id') ?? nodeId ?? `${this.slugify(label)}-${index}`),
        label,
        score: this.getPath(item, mapping.score || 'score'),
        color: this.getPath(item, mapping.color || 'color'),
        textColor: this.getPath(item, mapping.textColor || 'textColor'),
        nodeId: nodeId ? String(nodeId) : undefined,
        raw: item,
      };
    });
  }

  protected applyInitialState(): void {
    const initialDepth = Number(this.dashboardConfig?.selection?.initialExpandDepth ?? 1);
    this.expandToDepth(this.rootNodes, initialDepth);

    const defaultSelected = this.dashboardConfig?.selection?.defaultSelectedIds;
    if (Array.isArray(defaultSelected)) {
      defaultSelected.forEach((id: any) => {
        if (this.nodeLookup.has(String(id))) {
          this.selectedNodeIds.add(String(id));
        }
      });
    }
  }

  protected expandToDepth(nodes: NormalizedNode[], maxDepth: number): void {
    nodes.forEach((node) => {
      if (node.level < maxDepth && node.children.length) {
        this.expandedNodeIds.add(node.id);
      }
      if (node.children.length) {
        this.expandToDepth(node.children, maxDepth);
      }
    });
  }

  protected keepValidIds(ids: Set<string>): Set<string> {
    return new Set(Array.from(ids).filter((id) => this.nodeLookup.has(id)));
  }

  protected rebuildVisibleNodes(): void {
    const query = this.searchTerm.trim().toLowerCase();
    const rows: VisibleNode[] = [];

    const append = (node: NormalizedNode): boolean => {
      const matches = !query || node.label.toLowerCase().includes(query);
      const childMatches: NormalizedNode[] = [];

      for (const child of node.children) {
        if (this.nodeMatchesQuery(child, query)) {
          childMatches.push(child);
        }
      }

      const shouldShow = matches || childMatches.length > 0;
      if (!shouldShow) return false;

      rows.push({ node, level: node.level });

      const showChildren = query || this.expandedNodeIds.has(node.id);
      if (showChildren) {
        const childrenToRender = query ? childMatches : node.children;
        childrenToRender.forEach((child) => append(child));
      }

      return true;
    };

    this.rootNodes.forEach((node) => append(node));
    this.visibleNodes = rows;
    this.cdr.detectChanges();
  }

  protected nodeMatchesQuery(node: NormalizedNode, query: string): boolean {
    if (!query) return true;
    if (node.label.toLowerCase().includes(query)) return true;
    return node.children.some((child) => this.nodeMatchesQuery(child, query));
  }

  protected refreshContext(): void {
    this.chartSegments = this.buildChartSegments();
    this.infoCards = this.buildInfoCards();
    this.centerLabelLines = this.buildCenterLabel();
    this.renderDonutChart();
  }

  protected buildChartSegments(): DonutSegment[] {
    const nodes = this.getContextNodes();
    const palette = this.dashboardConfig?.colors || this.dashboardConfig?.donut?.colors || [
      '#d92f2f',
      '#2479cc',
      '#388e3c',
      '#f57c00',
      '#7b1fa2',
      '#455a64',
      '#00897b',
      '#c2185b',
    ];

    return nodes.map((node, index) => ({
      nodeId: node.id,
      label: node.label,
      value: node.value,
      color: this.resolveNodeColor(node, index, palette),
      raw: node.raw,
    }));
  }

  protected getContextNodes(): NormalizedNode[] {
    const topSelected = this.getTopSelectedNodes();
    if (topSelected.length === 1 && topSelected[0].children.length) {
      return topSelected[0].children;
    }
    if (topSelected.length > 0) {
      return topSelected;
    }

    const focusedNode = this.focusedNodeId ? this.nodeLookup.get(this.focusedNodeId) : undefined;
    if (focusedNode && focusedNode.children.length) {
      return focusedNode.children;
    }
    if (focusedNode) {
      return [focusedNode];
    }

    return this.rootNodes;
  }

  protected getTopSelectedNodes(): NormalizedNode[] {
    const selectedNodes = Array.from(this.selectedNodeIds)
      .map((id) => this.nodeLookup.get(id))
      .filter((node): node is NormalizedNode => !!node);

    return selectedNodes.filter((node) => {
      let parentId = node.parentId;
      while (parentId) {
        if (this.selectedNodeIds.has(parentId)) return false;
        parentId = this.nodeLookup.get(parentId)?.parentId;
      }
      return true;
    });
  }

  protected buildInfoCards(): InfoCard[] {
    const cfg = this.dashboardConfig?.infoCards || {};
    const definitionCards = this.buildConfiguredInfoCards(cfg);
    if (definitionCards.length > 0) return definitionCards;

    const cardMapping = cfg.cardMapping || {};
    const primaryNode = this.getPrimaryContextNode();
    const selectedSourcePath = cfg.selectedSourcePath || 'detailCards';
    const selectedCards = primaryNode ? this.getPath(primaryNode.raw, selectedSourcePath) : undefined;
    const globalCards = this.getPath(this.rootPayload, cfg.sourcePath || 'infoCards');
    const rawCards = cfg.preferSelected === false ? globalCards : (selectedCards ?? globalCards);
    const source = Array.isArray(rawCards) ? rawCards : rawCards ? [rawCards] : [];

    return source.map((card: any, index: number) => {
      const itemPath = cardMapping.items || 'items';
      const rawItems = this.getPath(card, itemPath);
      return {
        id: String(this.getPath(card, cardMapping.id || 'id') ?? `info-card-${index}`),
        title: String(this.getPath(card, cardMapping.title || 'title') ?? ''),
        position: this.getPath(card, cardMapping.position || 'position') ?? this.defaultCardPosition(index),
        items: this.normalizeInfoItems(rawItems, cfg.itemMapping || {}),
        raw: card,
      };
    });
  }

  protected normalizeInfoItems(rawItems: any, mapping: any): InfoCardItem[] {
    const source = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : [];
    return source.map((item: any, index: number) => {
      const label = String(
        this.getPath(item, mapping.label || 'label')
          ?? this.getPath(item, 'name')
          ?? `Metric ${index + 1}`,
      );
      const rawValue = this.getPath(item, mapping.value || 'value');
      const format = this.getPath(item, mapping.format || 'format') || item?.formatRule;
      return {
        label,
        value: this.formatValue(rawValue, format),
        rawValue,
      };
    });
  }

  protected buildConfiguredInfoCards(cfg: any): InfoCard[] {
    const definitions = Array.isArray(cfg.definitions) ? cfg.definitions : [];
    if (!definitions.length) return [];

    const primaryNode = this.getPrimaryContextNode();
    const matchingDefinitions = primaryNode && cfg.preferSelected !== false
      ? definitions.filter((definition: any) => this.infoCardDefinitionMatchesNode(definition, primaryNode))
      : [];
    const definitionsToRender = matchingDefinitions.length ? matchingDefinitions : definitions;

    return definitionsToRender.map((definition: any, index: number) => {
      const sourceNode = this.resolveInfoCardDefinitionNode(definition) || primaryNode;
      const source = definition.sourcePath
        ? this.getPath(this.rootPayload, definition.sourcePath)
        : sourceNode?.raw || this.rootPayload;
      const titleTemplate = definition.title || sourceNode?.label || `Details ${index + 1}`;
      const title = this.interpolate(titleTemplate, {
        label: sourceNode?.label || '',
        value: sourceNode?.value ?? '',
        source,
      });

      return {
        id: String(definition.id || `${this.slugify(title)}-${index}`),
        title,
        position: definition.position ?? this.defaultCardPosition(index),
        items: this.buildConfiguredInfoCardItems(definition.items, source),
        raw: source,
      };
    });
  }

  protected buildConfiguredInfoCardItems(items: any, source: any): InfoCardItem[] {
    const list = Array.isArray(items) ? items : [];
    return list.map((item: any, index: number) => {
      const rawValue = item.value !== undefined
        ? item.value
        : this.getPath(source, item.valuePath || item.key || item.path);
      return {
        label: String(item.label || item.key || item.path || `Metric ${index + 1}`),
        value: this.formatValue(rawValue, item.format),
        rawValue,
      };
    });
  }

  protected infoCardDefinitionMatchesNode(definition: any, node: NormalizedNode): boolean {
    if (definition.nodeId && String(definition.nodeId) === node.id) return true;
    if (definition.nodeLabel && this.sameText(definition.nodeLabel, node.label)) return true;
    if (definition.nodeField && definition.nodeValue !== undefined) {
      return this.sameText(this.getPath(node.raw, definition.nodeField), definition.nodeValue);
    }
    if (definition.match?.field && definition.match?.value !== undefined) {
      return this.sameText(this.getPath(node.raw, definition.match.field), definition.match.value);
    }
    return false;
  }

  protected resolveInfoCardDefinitionNode(definition: any): NormalizedNode | undefined {
    if (definition.nodeId) {
      return this.nodeLookup.get(String(definition.nodeId)) || this.findNodeByLabel(String(definition.nodeId));
    }
    if (definition.nodeLabel) {
      return this.findNodeByLabel(String(definition.nodeLabel));
    }
    if (definition.nodeField && definition.nodeValue !== undefined) {
      return Array.from(this.nodeLookup.values()).find((node) =>
        this.sameText(this.getPath(node.raw, definition.nodeField), definition.nodeValue),
      );
    }
    if (definition.match?.field && definition.match?.value !== undefined) {
      return Array.from(this.nodeLookup.values()).find((node) =>
        this.sameText(this.getPath(node.raw, definition.match.field), definition.match.value),
      );
    }
    return undefined;
  }

  protected buildCenterLabel(): string[] {
    const donutCfg = this.dashboardConfig?.donut || {};
    const cfg = donutCfg.centerLabel || {};
    const primaryNode = this.getPrimaryContextNode();
    const context = {
      label: primaryNode?.label || donutCfg.rootLabel || this.dashboardTitle,
      value: primaryNode?.value ?? this.chartSegments.reduce((sum, item) => sum + item.value, 0),
      count: this.chartSegments.length,
    };

    const lines = cfg.lines;
    if (Array.isArray(lines) && lines.length) {
      return lines.map((line: string) => this.interpolate(line, context));
    }

    return [
      String(context.label || ''),
      this.formatValue(context.value, cfg.valueFormat || donutCfg.valueFormat),
    ].filter((line) => !!line);
  }

  protected renderDonutChart(): void {
    if (!this.chartHost?.nativeElement) return;

    if (!this.chartSegments.length) {
      this.chart?.clear();
      return;
    }

    if (!this.chart) {
      this.chart = echarts.init(this.chartHost.nativeElement);
    }

    const donutCfg = this.dashboardConfig?.donut || {};
    const total = this.chartSegments.reduce((sum, segment) => sum + segment.value, 0);
    const labelMode = donutCfg.labelMode || 'percent';
    const labelFormat = donutCfg.labelFormat || donutCfg.valueFormat;
    const labelFontSize = Number(donutCfg.labelFontSize ?? 10);
    const labelLineHeight = Number(donutCfg.labelLineHeight ?? Math.max(labelFontSize + 1, 12));
    const labelWidth = Number(donutCfg.labelWidth ?? 74);

    const option: echarts.EChartsOption = {
      animation: donutCfg.animation !== false,
      animationDuration: donutCfg.animationDuration ?? 500,
      tooltip: {
        trigger: 'item',
        
        formatter: (params: any) => this.formatTooltip(params, total),
      },
      legend: {
        show: false,
      },
      series: [
        {
          type: 'pie',
          radius: donutCfg.radius || ['42%', '70%'],
          center: donutCfg.center || ['50%', '50%'],
          startAngle: donutCfg.startAngle ?? 90,
          clockwise: donutCfg.clockwise !== false,
          avoidLabelOverlap: true,
          minAngle: donutCfg.minAngle ?? 4,
          data: this.chartSegments.map((segment) => ({
            name: segment.label,
            value: segment.value,
            nodeId: segment.nodeId,
            raw: segment.raw,
            itemStyle: {
              color: segment.color,
              borderColor: '#fff',
              borderWidth: 1,
            },
          })),
          label: {
            show: donutCfg.showLabels !== false,
            position: donutCfg.labelPosition || 'inside',
            color: donutCfg.labelColor || '#fff',
            fontSize: labelFontSize,
            fontWeight: donutCfg.labelFontWeight || 700,
            lineHeight: labelLineHeight,
            width: labelWidth,
            overflow: donutCfg.labelOverflow || 'break',
            ellipsis: donutCfg.labelEllipsis || '...',
            align: 'center',
            formatter: (params: any) => this.formatDonutLabel(params, total, labelMode, labelFormat, donutCfg),
          },
          labelLine: {
            show: donutCfg.labelPosition !== 'inside' && donutCfg.showLabelLine !== false,
          },
          emphasis: {
            scale: true,
            scaleSize: 6,
            itemStyle: {
              shadowBlur: 14,
              shadowColor: 'rgba(0, 0, 0, 0.25)',
            },
          },
        } as any,
      ],
    };

    this.chart.setOption(option, true);
    this.chart.off('click');
    this.chart.on('click', (params: any) => {
      const node = this.nodeLookup.get(String(params?.data?.nodeId || ''));
      if (node) this.toggleNodeSelection(node);
    });

    setTimeout(() => this.resizeChart(), 0);
  }

  protected formatDonutLabel(params: any, total: number, labelMode: string, labelFormat: any, donutCfg: any): string {
    if (labelMode === 'none') return '';

    const name = String(params?.name || '');
    const value = Number(params?.value || 0);
    const percentage = total > 0 ? (value / total) * 100 : 0;
    const maxLineLength = Number(donutCfg.labelMaxLineLength ?? 10);
    const maxLines = Number(donutCfg.labelMaxLines ?? 2);
    const wrappedName = this.wrapDonutLabel(name, maxLineLength, maxLines);

    if (labelMode === 'name') return wrappedName;

    const formattedValue = labelMode === 'value'
      ? this.formatValue(value, labelFormat || donutCfg.valueFormat)
      : this.formatValue(percentage, labelFormat || { type: 'percent', decimals: 0 });

    return [wrappedName, formattedValue].filter(Boolean).join('\n');
  }

  protected wrapDonutLabel(label: string, maxLineLength: number, maxLines: number): string {
    if (!label) return '';

    const words = label.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';

    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length <= maxLineLength) {
        current = candidate;
        return;
      }

      if (current) lines.push(current);
      current = word;
    });

    if (current) lines.push(current);

    return lines
      .slice(0, maxLines)
      .map((line, index) => {
        const isLastVisibleLine = index === maxLines - 1 && lines.length > maxLines;
        return this.truncateLabelLine(line, maxLineLength, isLastVisibleLine);
      })
      .join('\n');
  }

  protected truncateLabelLine(value: string, maxLength: number, addEllipsis: boolean): string {
    if (value.length <= maxLength && !addEllipsis) return value;
    if (maxLength <= 1) return value.slice(0, maxLength);
    const suffix = addEllipsis ? '...' : '';
    return `${value.slice(0, Math.max(1, maxLength - suffix.length))}${suffix}`;
  }

  protected formatTooltip(params: any, total: number): string {
    const donutCfg = this.dashboardConfig?.donut || {};
    const fields = donutCfg.tooltip?.fields;
    const raw = params?.data?.raw || {};
    const value = Number(params?.value || 0);
    const context = {
      ...raw,
      _name: params?.name,
      _value: value,
      _percentage: total > 0 ? (value / total) * 100 : 0,
    };

    if (Array.isArray(fields) && fields.length) {
      const rows = fields.map((field: any) => {
        const rawValue = this.getPath(context, field.key || field.path);
        return `<div><strong>${field.label || field.key}:</strong> ${this.formatValue(rawValue, field)}</div>`;
      });

      return `<div>${params?.marker || ''}<strong>${params?.name || ''}</strong></div>${rows.join('')}`;
    }

    return `${params?.marker || ''}${params?.name || ''}: ${this.formatValue(value, donutCfg.valueFormat)}`;
  }

  protected resizeChart(): void {
    setTimeout(() => this.chart?.resize(), 0);
  }

  protected focusNode(node: NormalizedNode, rebuild = true): void {
    this.focusedNodeId = node.id;
    if (rebuild) {
      this.refreshContext();
    }
  }

  protected emitSelection(): void {
    if (!this.data?.filterKey) return;

    const topSelected = this.getTopSelectedNodes();
    const selectedPayload = topSelected.map((node) => ({
      ...node.raw,
      id: node.id,
      label: node.label,
      value: node.value,
    }));

    if (!selectedPayload.length) {
      this.chartFilterHandler.updateFilterSelection(this.data, []);
      return;
    }

    if (this.selectionMode === 'single' && selectedPayload[0]) {
      this.chartFilterHandler.updateFilterSelection(this.data, selectedPayload[0], selectedPayload[0]);
      return;
    }

    const groupedPayload = this.buildGroupedFilterPayload(selectedPayload);
    this.chartFilterHandler.updateFilterSelection(this.data, groupedPayload, groupedPayload);
  }

  protected buildGroupedFilterPayload(selectedPayload: any[]): Record<string, any[]> {
    const keys = this.getConfiguredFilterKeys();
    if (!keys.length) {
      return { value: selectedPayload };
    }

    return keys.reduce((result: Record<string, any[]>, key) => {
      result[key] = selectedPayload.map((item) => item?.[key] ?? item?.id ?? item?.label);
      return result;
    }, {});
  }

  protected getConfiguredFilterKeys(): string[] {
    if (Array.isArray(this.data?.filterKey)) {
      return this.data.filterKey.map((key: any) => String(key));
    }

    return String(this.data?.filterKey || '')
      .split(',')
      .map((key) => key.trim())
      .filter((key) => !!key);
  }

  protected getPrimaryContextNode(): NormalizedNode | undefined {
    const topSelected = this.getTopSelectedNodes();
    if (topSelected[0]) return topSelected[0];
    if (this.focusedNodeId) return this.nodeLookup.get(this.focusedNodeId);
    return undefined;
  }

  protected defaultCardPosition(index: number): string {
    const positions = ['top', 'right', 'right-bottom', 'bottom', 'left-bottom', 'left'];
    return positions[index % positions.length];
  }

  protected resolveNodeColor(node: NormalizedNode, index: number, palette: string[]): string {
    const colorMap = this.dashboardConfig?.colorMap || this.dashboardConfig?.donut?.colorMap || {};
    return node.color
      || colorMap[node.id]
      || colorMap[node.label]
      || palette[index % palette.length]
      || '#607d8b';
  }

  protected resolveInfoCardAccent(card: InfoCard): string {
    const colorMap = this.dashboardConfig?.colorMap || this.dashboardConfig?.donut?.colorMap || {};
    const matchedNode = this.findNodeByLabel(card.title)
      || (card.raw ? Array.from(this.nodeLookup.values()).find((node) => node.raw === card.raw) : undefined);
    return matchedNode?.color
      || (matchedNode ? colorMap[matchedNode.id] || colorMap[matchedNode.label] : undefined)
      || colorMap[card.title]
      || '#626262';
  }

  protected getThresholdStyle(value: any): Record<string, string> {
    const thresholds = this.dashboardConfig?.thresholds;
    const numberValue = Number(value);
    if (!Array.isArray(thresholds) || isNaN(numberValue)) return {};

    const match = thresholds.find((threshold: any) => {
      const min = threshold.min ?? Number.NEGATIVE_INFINITY;
      const max = threshold.max ?? Number.POSITIVE_INFINITY;
      return numberValue >= min && numberValue <= max;
    });

    if (!match) return {};
    return {
      'background-color': match.color || '#d8d8d8',
      color: match.textColor || '#1f1f1f',
    };
  }

  protected collectDescendantIds(node: NormalizedNode): string[] {
    const ids: string[] = [];
    const visit = (current: NormalizedNode): void => {
      current.children.forEach((child) => {
        ids.push(child.id);
        visit(child);
      });
    };
    visit(node);
    return ids;
  }

  protected ensureAncestorsExpanded(node: NormalizedNode): void {
    let parentId = node.parentId;
    while (parentId) {
      this.expandedNodeIds.add(parentId);
      parentId = this.nodeLookup.get(parentId)?.parentId;
    }
  }

  protected findNodeByLabel(label: string): NormalizedNode | undefined {
    const normalizedLabel = this.normalizeText(label);
    if (!normalizedLabel) return undefined;

    return this.rootNodes.find((node) => this.normalizeText(node.label) === normalizedLabel)
      || Array.from(this.nodeLookup.values()).find((node) => this.normalizeText(node.label) === normalizedLabel);
  }

  protected sameText(left: any, right: any): boolean {
    return this.normalizeText(left) === this.normalizeText(right);
  }

  protected normalizeText(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  protected getPath(source: any, path?: string, fallback?: any): any {
    if (!path) return source ?? fallback;
    if (source === undefined || source === null) return fallback;

    const normalized = String(path).replace(/\[(\d+)\]/g, '.$1');
    const parts = normalized.split('.').filter(Boolean);
    let cursor = source;

    for (const part of parts) {
      if (cursor === undefined || cursor === null) return fallback;
      cursor = cursor[part];
    }

    return cursor === undefined ? fallback : cursor;
  }

  protected formatValue(value: any, rule?: FormatRule): string {
    if (rule?.template) {
      return this.interpolate(rule.template, { value });
    }

    if (value === undefined || value === null || value === '') return '-';

    const type = rule?.type || 'text';
    const numeric = Number(value);
    const isNumeric = !isNaN(numeric) && isFinite(numeric);

    if (type === 'text' || !isNumeric) {
      return `${rule?.prefix || ''}${String(value)}${rule?.suffix || ''}`;
    }

    const scaled = numeric / Number(rule?.scale || 1);
    const locale = rule?.locale || 'en-US';
    const decimals = rule?.decimals ?? (type === 'currency' ? 0 : 1);

    let formatted: string;
    if (type === 'currency') {
      formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: rule?.currency || 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(scaled);
    } else if (type === 'compact') {
      formatted = new Intl.NumberFormat(locale, {
        notation: 'compact',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(scaled);
    } else {
      formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(scaled);
    }

    const suffix = rule?.suffix ?? (type === 'percent' ? '%' : '');
    return `${rule?.prefix || ''}${formatted}${suffix}`;
  }

  protected interpolate(template: string, context: Record<string, any>): string {
    return String(template).replace(/\{([^}]+)\}/g, (_match, key) => {
      const raw = key === 'value'
        ? context[key]
        : this.getPath(context, String(key), '');
      return raw === undefined || raw === null ? '' : String(raw);
    });
  }

  protected toNumber(value: any): number {
    const parsed = Number(value);
    return isNaN(parsed) || !isFinite(parsed) ? 0 : parsed;
  }

  protected slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      || 'node';
  }
}
